/**
 * Noten je Persona aus einem eingefrorenen Bericht lesen.
 *
 * Das Feld `personen` gibt es erst in Berichten ab dem 26.09.2026. Aeltere
 * Snapshots werden nie umgeschrieben (sie sind eingefroren, siehe report.ts);
 * fuer sie liefert diese Funktion eine leere Liste, und die Seite laesst den
 * Abschnitt weg. Kaputte Zeilen fallen einzeln heraus, statt den Bericht zu
 * sprengen: die Nutzlast kommt aus JSON, nicht aus einem Typ.
 *
 * EINE ZEILE JE PERSONA, NICHT JE URTEIL (seit 09.10.2026, Scopevisio). In der
 * Matrix (Persona × Job) urteilt dieselbe Persona mehrmals, und `personen`
 * traegt je Urteil eine Zeile mit demselben Namen. Der Bericht zeigte so 48
 * Zeilen fuer 18 Personas und schrieb „34 Personas bewerten …", wo 34 Urteile
 * gemeint waren. Jetzt werden Zeilen mit gleichem Namen zusammengefasst: Mittel
 * beider Noten, Abstand der Mittel, und wie viele der Urteile welche Fassung
 * waehlen. Das geht auch fuer schon eingefrorene Berichte, weil der Name je
 * Persona gleich bleibt. Eine Persona mit einem Urteil sieht aus wie vorher.
 */

export interface PersonaNote {
  persona: string;
  /** Nur bei Randpersonas (seit 27.09.2026). Alte Berichte haben es nie. */
  rolle?: 'rand';
  /** Bei mehreren Urteilen das Mittel. */
  geprueft: number;
  vergleich: number;
  /** geprueft minus vergleich, auf eine Stelle gerundet. */
  delta: number;
  /** Bei mehreren Urteilen die Mehrheit; Gleichstand oder ohne Angabe null. */
  gewinner: 'geprueft' | 'vergleich' | null;
  /** Nur bei mehr als einem Urteil: wie viele, und wie viele welche Fassung waehlen. */
  urteile?: number;
  fuerGeprueft?: number;
  fuerVergleich?: number;
}

const zahl = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const rund = (n: number) => Math.round(n * 10) / 10;

interface Zeile { persona: string; rand: boolean; geprueft: number; vergleich: number; gewinner: 'geprueft' | 'vergleich' | null }

/** Die gueltigen Zeilen, je Urteil eine. */
function zeilen(variante: unknown): Zeile[] {
  const roh = (variante as { personen?: unknown } | null)?.personen;
  if (!Array.isArray(roh)) return [];
  const aus: Zeile[] = [];
  for (const p of roh) {
    const g = zahl(p?.geprueft);
    const v = zahl(p?.vergleich);
    if (g == null || v == null || typeof p?.persona !== 'string') continue;
    aus.push({
      persona: p.persona,
      rand: p.rolle === 'rand',
      geprueft: g,
      vergleich: v,
      gewinner: p.gewinner === 'geprueft' || p.gewinner === 'vergleich' ? p.gewinner : null,
    });
  }
  return aus;
}

/** Wie viele Urteile eine Fassung traegt: je Persona und Kaufanlass eines. */
export function anzahlUrteile(variante: unknown): number {
  return zeilen(variante).length;
}

/**
 * Sortiert nach Abstand: wer am staerksten anzieht zuerst, wer am deutlichsten
 * zurueckgeht zuletzt. Hat der Lauf Randpersonas, stehen sie danach (das
 * Urteil folgt dem Kern), innerhalb jeder Gruppe wieder nach Abstand.
 */
export function personaNoten(variante: unknown): PersonaNote[] {
  const gruppen = new Map<string, Zeile[]>();
  for (const z of zeilen(variante)) gruppen.set(z.persona, [...(gruppen.get(z.persona) ?? []), z]);
  const aus: PersonaNote[] = [...gruppen.entries()].map(([persona, zs]) => {
    const rand = zs.some((z) => z.rand);
    if (zs.length === 1) {
      const z = zs[0]!;
      return {
        persona,
        ...(rand ? { rolle: 'rand' as const } : {}),
        geprueft: z.geprueft,
        vergleich: z.vergleich,
        delta: rund(z.geprueft - z.vergleich),
        gewinner: z.gewinner,
      };
    }
    const g = zs.reduce((s, z) => s + z.geprueft, 0) / zs.length;
    const v = zs.reduce((s, z) => s + z.vergleich, 0) / zs.length;
    const fuerGeprueft = zs.filter((z) => z.gewinner === 'geprueft').length;
    const fuerVergleich = zs.filter((z) => z.gewinner === 'vergleich').length;
    return {
      persona,
      ...(rand ? { rolle: 'rand' as const } : {}),
      geprueft: rund(g),
      vergleich: rund(v),
      delta: rund(g - v),
      gewinner: fuerGeprueft > fuerVergleich ? 'geprueft' : fuerVergleich > fuerGeprueft ? 'vergleich' : null,
      urteile: zs.length,
      fuerGeprueft,
      fuerVergleich,
    };
  });
  const r = (p: PersonaNote) => Number(p.rolle === 'rand');
  return aus.sort((a, b) => r(a) - r(b) || b.delta - a.delta || a.persona.localeCompare(b.persona, 'de'));
}
